/**
 * @file dashboard.tsx
 * @module app/modules/sales-management/sales-manager/dashboard/dashboard
 * @description Implements the dashboard interface for sales-management operations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { useEffect, useState } from "react";
import { Button, Card, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import APICallService from "../../../../../api/apiCallService";
import { ASSIST_ADMIN_DASHBOARD, ordersDelivery } from "../../../../../api/apiEndPoints";
import Loader from "../../../../../Global/loader";
import { Manager, OrderCancelled, OrderDelivered, OrderOutForDelivery, OrderProcessed, SalesRepOrders } from "../../../../../utils/constants";
import Method from "../../../../../utils/methods";
import { orderStatusJSON } from "../../../../../utils/staticJSON";
import DistrictWiseOrderTrend from "./chart/district-wise-order-trend";
import SalesTrend from "./chart/sales-trend";
import TargetCompletion from "./chart/target-completion";
import { useAuth } from "../../../auth";
import { useNavigationHistory } from "../../../../../utils/useNavigationHistory";
const Dashboard = () => {
  const navigate = useNavigate();
  const { navigateTo } = useNavigationHistory();
  const { currentUser }: any = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [fetchLoader, setFetchLoader] = useState<boolean>(false);
  const [orders, setOrders] = useState<Array<any>>([]);
  const [count, setCount] = useState({
    runningRate: 0,
    salesTarget: {
      currentSalesTarget: {
        achieved: 0,
        target: 0
      }
    },
    totalActiveCustomers: 0,
    totalInactiveCustomers: 0,
    totalNewCustomers: 0,
    totalOrders: 0,
    totalSales: 0,
    totalSalesManager: 0,
    totalSalesRep: 0,
    totalActivationManager: 0,
    totalActivationRep: 0,
    totalSalesOfCurrentMonth: 0
  });
  useEffect(() => {
    (async () => {
      await fetchData();
    })();
  }, []);
  const fetchData = async () => {
    let params: any = {
      needStats: true,
      needAllStats: true,
      fromDate: Method.convertDateToFormat(new Date(), "YYYY-MM-DD"),
    };
    setLoading(true);
    let apiService = new APICallService(
      ASSIST_ADMIN_DASHBOARD,
      "",
      "",
      "",
      "",
      "",
      Dashboard
    );
    let response = await apiService.callAPI();
    if (response) {
      setCount(response);
    }
    setLoading(false);
  };
  useEffect(() => {
    (async () => {
      setFetchLoader(true);
      await fetchOrdersList();
      setFetchLoader(false);
    })();
  }, []);
  const fetchOrdersList = async () => {
    setLoading(true);
    let params: any = {
      pageNo: 1,
      limit: 10,
      sortKey: "_createdAt",
      sortOrder: -1,
      needCount: false,
      manager: currentUser?._id
    };

    let apiService = new APICallService(
      ordersDelivery.listAssistAdmin,
      params,
      "",
      "",
      "",
      "",
      SalesRepOrders
    );
    let response: any = await apiService.callAPI();
    if (response.records) {
      setOrders(response.records);
    }
    setLoading(false);
  };
  const getStatusBadge = (status: any) => {
    const statusObj = orderStatusJSON.find((val) => val.value === status);
    if (!statusObj)
      return (
        <div className="badge rounded-pill bg-light-secondary text-secondary fs-14 p-4">
          NA
        </div>
      );

    let badgeClass = "bg-light-primary text-primary";
    switch (status) {
      case OrderProcessed:
        badgeClass = "bg-light-warning text-warning";
        break;
      case OrderOutForDelivery:
        badgeClass = "bg-light-info text-info";
        break;
      case OrderDelivered:
        badgeClass = "bg-light-success text-success";
        break;
      case OrderCancelled:
        badgeClass = "bg-light-danger text-danger";
        break;
      default:
        badgeClass = "bg-light-primary text-primary";
    }

    return (
      <div className={`badge rounded-pill ${badgeClass} fs-14 py-2 px-4`}>
        {statusObj.label}
      </div>
    );
  };
  const renderTooltip = (props: any) => (
    <Tooltip className="tooltip w-lg-375px" {...props}>
      <p className="text-white mb-0 fs-14 fw-500">
        Orders from customers in different districts will still count toward the assigned {currentUser?.role.some((obj: any) => obj.type === Manager) ? "sales" : "activation"} representative’s target.
      </p>
    </Tooltip>
  );
  return (
    <div className="p-5">
      <Row className="align-items-center mb-7 g-5">
        <Col xs className="fs-22 fw-bolder">
          Dashboard
        </Col>
      </Row>
      {loading ? (
        <div className="w-100 d-flex justify-content-center">
          <Loader loading={loading} />
        </div>
      ) : (
        <>
          {" "}
          <Row className="align-items-center mb-7 g-6">
            <Col xl={3} md={6} sm={4}>
              <div className="border-r8px bg-c9f3d7 rounded py-4 px-5">
                <div className="d-flex justify-content-between">
                  <div className="d-flex flex-column">
                    <span className="fs-22 fw-bolder">
                      TSh{" "}
                      {count?.salesTarget?.currentSalesTarget?.target > 0 ? Method.getGeneralizedAmount(count?.salesTarget?.currentSalesTarget?.target) : 0}
                    </span>
                    <span className="fw-medium fs-16">Total target</span>
                  </div>
                  {/* <img src={IMAGES.SellerSVG} alt="" /> */}
                </div>
              </div>
            </Col>
            <Col xl={3} md={6} sm={4}>
              <div className="border-r8px bg-fad4fc rounded py-4 px-5 position-relative">
                <span className="position-absolute top-0 end-0 mt-3 me-3 d-inline-flex align-items-center justify-content-center cursor-pointer">
                  <OverlayTrigger placement="top" overlay={renderTooltip}>
                    <i className="fas fa-info-circle fs-2 text-dark"></i>
                  </OverlayTrigger>
                </span>
                <div className="d-flex flex-column">
                  <span className="fs-22 fw-bolder">
                    TSh{" "}
                    {count?.salesTarget?.currentSalesTarget?.achieved > 0
                      ? Method.getGeneralizedAmount(
                        count?.salesTarget?.currentSalesTarget?.achieved
                      )
                      : 0}
                  </span>
                  <span className="fw-medium fs-16">
                    {`Achieved So Far`}{" "}
                  </span>
                </div>
              </div>
            </Col>
            <Col xl={3} md={6} sm={4}>
              <div className="border-r8px bg-fbe5e5 rounded py-4 px-5">
                <div className="d-flex justify-content-between">
                  <div className="d-flex flex-column">
                    <span className="fs-22 fw-bolder">
                      TSh{" "}
                      {count?.salesTarget?.currentSalesTarget?.target > 0 ? Method.getGeneralizedAmount(Math.max(
                        0,
                        (count?.salesTarget?.currentSalesTarget?.target ?? 0) -
                        (count?.salesTarget?.currentSalesTarget?.achieved ?? 0)
                      )) : 0}
                    </span>
                    <span className="fw-medium fs-16">Remaining</span>
                  </div>
                  {/* <img src={IMAGES.SellerSVG} alt="" /> */}
                </div>
              </div>
            </Col>
            <Col xl={3} md={6} sm={4}>
              <div className="border-r8px bg-fce7d4 rounded py-4 px-5">
                <div className="d-flex justify-content-between">
                  <div className="d-flex flex-column">
                    <span className="fs-22 fw-bolder">
                      TSh {count?.runningRate > 0
                        ? Method.getGeneralizedAmount(count?.runningRate)
                        : 0}
                    </span>
                    <span className="fw-medium fs-16">
                      Current Month Running Rate
                    </span>
                  </div>
                  {/* <img src={IMAGES.CustomerSVG} alt="" /> */}
                </div>
              </div>
            </Col>
            <Col xl={3} md={6} sm={4}>
              <div className="border-r8px bg-d4e1fc rounded py-4 px-5">
                <div className="d-flex justify-content-between">
                  <div className="d-flex flex-column">
                    <span className="fs-22 fw-bolder">
                      {count?.totalActiveCustomers > 0 ? count?.totalActiveCustomers : 0}
                    </span>
                    <span className="fw-medium fs-16">Total Assigned Customers</span>
                  </div>
                  {/* <img src={IMAGES.ProductSVG} alt="" /> */}
                </div>
              </div>
            </Col>
            <Col xl={3} md={6} sm={4}>
              <div className="border-r8px bg-ccebfd  rounded py-4 px-5">
                <div className="d-flex justify-content-between">
                  <div className="d-flex flex-column">
                    <span className="fs-22 fw-bolder">
                      {count?.totalNewCustomers > 0 ? count?.totalNewCustomers : 0}
                    </span>
                    <span className="fw-medium fs-16">New Customers (in last 7 days)</span>
                  </div>
                  {/* <img src={IMAGES.OrderSVG} alt="" /> */}
                </div>
              </div>
            </Col>
            <Col xl={3} md={6} sm={4}>
              <div className="border-r8px bg-ccf3f0  rounded py-4 px-5">
                <div className="d-flex justify-content-between">
                  <div className="d-flex flex-column">
                    <span className="fs-22 fw-bolder">
                      {count?.totalOrders > 0 ? count?.totalOrders : 0} (TSh {count?.totalSales > 0 ? Method.getGeneralizedAmount(count?.totalSales) : 0})
                    </span>
                    <span className="fw-medium fs-16">Total Orders</span>
                  </div>
                  {/* <img src={IMAGES.OrderSVG} alt="" /> */}
                </div>
              </div>
            </Col>
            {currentUser?.role.some((obj: any) => obj.type === Manager) ? (
              <Col xl={3} md={6} sm={4}>
                <div className="border-r8px bg-fbeabc  rounded py-4 px-5">
                  <div className="d-flex justify-content-between">
                    <div className="d-flex flex-column">
                      <span className="fs-22 fw-bolder">
                        {count?.totalSalesRep > 0 ? count?.totalSalesRep : 0}
                      </span>
                      <span className="fw-medium fs-16">Total Sales Reps</span>
                    </div>
                    {/* <img src={IMAGES.OrderSVG} alt="" /> */}
                  </div>
                </div>
              </Col>
            ) : (
              <Col xl={3} md={6} sm={4}>
                <div className="border-r8px bg-fbeabc  rounded py-4 px-5">
                  <div className="d-flex justify-content-between">
                    <div className="d-flex flex-column">
                      <span className="fs-22 fw-bolder">
                        {count?.totalActivationRep > 0 ? count?.totalActivationRep : 0}
                      </span>
                      <span className="fw-medium fs-16">Total Activation Reps</span>
                    </div>
                    {/* <img src={IMAGES.OrderSVG} alt="" /> */}
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </>
      )}
      <Row className="align-items-center my-7 g-6">
        <SalesTrend />
        <DistrictWiseOrderTrend />
      </Row>
      <TargetCompletion />

      <Row className="align-items-center my-7 g-5">
        <Col xs>
          <div className="d-flex justify-content-between mb-5">
            <h1 className="fs-22 fw-bolder">Recent Orders</h1>
            <Button
              variant="primary"
              className="uw-btn uw-btn-primary uw-btn-lg fs-16 fw-bold btn-lg"
              onClick={() => {
                navigate("/sales-management/manager/orders");
              }}
            >
              View All
            </Button>
          </div>
          {!fetchLoader ? (
            orders && orders.length ? (
              <>
                <Card className="border border-r10px">
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <table className="table table-rounded table-row-bordered align-middle gs-7 gy-6 mb-0">
                        <thead>
                          <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                            <th className="min-w-md-175px min-w-150px">
                              Order Date & ID
                            </th>
                            <th className="min-w-md-200px min-w-125px">
                              Customer name
                            </th>
                            <th className="min-w-md-125px">Rep Name</th>
                            <th className="min-w-md-125px min-w-100px">
                              Total amount
                            </th>
                            <th className="min-w-md-75px min-w-50px">Status</th>
                            <th className="min-w-100px text-end"></th>
                          </tr>
                        </thead>
                        <tbody>
                          <>
                            {orders.map((orderVal: any, goodIndex: number) => (
                              <tr key={goodIndex}>
                                <td className="fs-15 fw-500">
                                  <div className="d-flex align-items-start flex-column">
                                    <span className="text-dark d-block">
                                      {Method.convertDateToDDMMYYYYHOURS(
                                        orderVal._createdAt
                                      )}
                                    </span>
                                    <span className="text-gray d-block">
                                      {orderVal.refKey}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <span className="fs-15 fw-500 d-block">
                                    {orderVal.customer.name}
                                  </span>
                                </td>
                                <td>
                                  <span className="fs-15 fw-500 badge rounded-pill bg-light-secondary text-black p-4 h-10">
                                    {orderVal?.customer?.assignedTo?.name}
                                  </span>
                                </td>
                                <td>
                                  <span className="fs-15 fw-500">
                                    {"TSh " +
                                      Method.getGeneralizedAmount(
                                        orderVal.payment.totalCharge
                                      )}
                                  </span>
                                </td>
                                <td>{getStatusBadge(orderVal.status)}</td>
                                <td className="text-end">
                                  <Button
                                    className="uw-btn uw-btn-primary fs-14 fw-600"
                                    onClick={() => {
                                      navigateTo("/orders/order-details", {
                                        state: {
                                          id: orderVal._id,
                                        },
                                      });
                                    }}
                                  >
                                    View details
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </>
                        </tbody>
                      </table>
                    </div>
                  </Card.Body>
                </Card>
              </>
            ) : (
              <>
                <div className="border border-r10px mb-6">
                  <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
                    <span className="fs-18 fw-500">No Data Found</span>
                  </div>
                </div>
              </>
            )
          ) : (
            <div className="border border-r10px mb-6">
              <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
                <Loader loading={fetchLoader || loading} />
              </div>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};
export default Dashboard;
